import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Icon from '../../../atoms/Icon'
import { FILTER_PATHS, lookupFilterPath, CONTEXT_GROUPINGS } from '../lib/scopeFilterCatalog'
import { PREDICATE_FAMILIES, OPERATORS_BY_TYPE } from '../lib/scopeFilterPredicates'

/**
 * One predicate row inside a filter-collection. Renders:
 *   - Path picker (select dropdown over the catalog; falls back to free
 *     text for paths the catalog doesn't surface)
 *   - Operator picker (filtered by the path's type family)
 *   - Value editor (haystack / needles / single / range / lower / upper),
 *     dispatched on the operator's `valueShape`
 *   - Remove button
 *   - Advanced disclosure (collapsed by default) with per-predicate
 *     `context` (which grouping level to evaluate at) and `change`
 *     overrides (date range)
 *
 * The wire-format rules we maintain are documented in scopeFilterCompile.js
 * -- this row's job is to keep the in-memory predicate object aligned with
 * what the compiler expects.
 */
export default function FilterBuilderRow ({ predicate, onChange, classes }) {
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const catalogEntry = useMemo(() => lookupFilterPath(predicate.path), [predicate.path])
  const inferredFamilyName = useMemo(() => {
    if (catalogEntry?.type) return catalogEntry.type
    // No catalog entry -- infer family from the current operator $type so
    // round-trips of free-text paths still render the right operator list.
    return OPERATORS_BY_TYPE[predicate.$type]?.family || 'string'
  }, [catalogEntry, predicate.$type])

  const family = PREDICATE_FAMILIES[inferredFamilyName] || PREDICATE_FAMILIES.string
  const operatorEntry = OPERATORS_BY_TYPE[predicate.$type]?.operator
  const valueShape = operatorEntry?.valueShape || 'haystack'

  const handlePathChange = useCallback((nextPath) => {
    const nextEntry = lookupFilterPath(nextPath)
    const nextFamily = nextEntry?.type
    if (nextFamily && nextFamily !== inferredFamilyName) {
      // Type family changed -- reset the operator + value shape to the
      // family's first operator and a clean default value.
      const firstOp = PREDICATE_FAMILIES[nextFamily].operators[0]
      const defaultValue = PREDICATE_FAMILIES[nextFamily].defaultValue?.() || {}
      onChange({
        $type: firstOp.$type,
        path: nextPath,
        ...defaultValue,
        ...(predicate.context && predicate.context !== 'Self' ? { context: predicate.context } : {}),
        ...(predicate.change ? { change: predicate.change } : {})
      })
    } else {
      onChange({ ...predicate, path: nextPath })
    }
  }, [predicate, inferredFamilyName, onChange])

  const handleOperatorChange = useCallback((nextType) => {
    const nextOpEntry = OPERATORS_BY_TYPE[nextType]?.operator
    if (!nextOpEntry) return
    // Strip value-shape-specific fields when the shape changes; carry over
    // path / context / change.
    const { path, context, change } = predicate
    const next = { $type: nextType, path }
    if (context && context !== 'Self') next.context = context
    if (change) next.change = change
    onChange(next)
  }, [predicate, onChange])

  const setValue = useCallback((patch) => {
    onChange({ ...predicate, ...patch })
  }, [predicate, onChange])

  const handleContextChange = useCallback((nextContext) => {
    const next = { ...predicate }
    if (!nextContext || nextContext === 'Self') delete next.context
    else next.context = nextContext
    onChange(next)
  }, [predicate, onChange])

  return (
    <div className={classes.predicateRow}>
      <div className={classes.predicateRowMain}>
        {/* Path picker -- catalog options + free-text fallback through datalist. */}
        <input
          className={classes.predicateField}
          list='scope-filter-path-options'
          placeholder='Path...'
          value={predicate.path || ''}
          onChange={(e) => handlePathChange(e.target.value)}
          style={{ minWidth: 180 }}
        />
        <datalist id='scope-filter-path-options'>
          {FILTER_PATHS.map(p => (
            <option key={p.path} value={p.path}>{p.label}</option>
          ))}
        </datalist>

        {/* Operator picker, filtered by the inferred family. */}
        <select
          className={classes.predicateField}
          value={predicate.$type}
          onChange={(e) => handleOperatorChange(e.target.value)}
        >
          {family.operators.map(op => (
            <option key={op.$type} value={op.$type}>{op.label}</option>
          ))}
        </select>

        {/* Value editor switches on operator's valueShape. */}
        <ValueEditor
          shape={valueShape}
          predicate={predicate}
          familyName={inferredFamilyName}
          setValue={setValue}
          classes={classes}
        />

        <span
          className={classes.removeBtn}
          onClick={() => onChange(null)}
          role='button'
          tabIndex={0}
          aria-label='Remove predicate'
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(null) } }}
        >
          <Icon name='close' customSize={14} />
        </span>
      </div>

      <div
        className={classes.advancedToggle}
        onClick={() => setAdvancedOpen(v => !v)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdvancedOpen(v => !v) } }}
      >
        {advancedOpen ? '\u25BC' : '\u25B6'} Advanced
      </div>
      {advancedOpen && (
        <div className={classes.advancedPanel}>
          <div className={classes.advancedField}>
            <label>Apply at:</label>
            <select
              className={classes.predicateField}
              value={predicate.context || 'Self'}
              onChange={(e) => handleContextChange(e.target.value)}
            >
              {CONTEXT_GROUPINGS.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          {/*
            Per-predicate change overrides (date range, performance options)
            are intentionally minimal in v1 -- only the date controls are
            exposed. Wider option overrides (annualize, gross/net of fees,
            etc.) are reachable today by editing the saved view JSON; a
            richer editor is a follow-up if usage justifies it.
          */}
          <ChangeOverrideEditor predicate={predicate} setValue={setValue} classes={classes} />
        </div>
      )}
    </div>
  )
}

FilterBuilderRow.propTypes = {
  predicate: PropTypes.shape({
    $type: PropTypes.string.isRequired,
    path: PropTypes.string,
    haystack: PropTypes.array,
    needles: PropTypes.array,
    lower: PropTypes.any,
    upper: PropTypes.any,
    value: PropTypes.any,
    context: PropTypes.string,
    change: PropTypes.object
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

// --- Value editors per shape ---

function ValueEditor ({ shape, predicate, familyName, setValue, classes }) {
  if (shape === 'haystack') {
    return (
      <ListInput
        items={predicate.haystack || []}
        onChange={(next) => setValue({ haystack: next })}
        classes={classes}
        placeholder='value(s)...'
      />
    )
  }
  if (shape === 'needles') {
    return (
      <ListInput
        items={predicate.needles || []}
        onChange={(next) => setValue({ needles: next })}
        classes={classes}
        placeholder='substring(s)...'
      />
    )
  }
  if (shape === 'single') {
    return (
      <SingleValueInput
        familyName={familyName}
        value={predicate.value}
        onChange={(v) => setValue({ value: v })}
        classes={classes}
      />
    )
  }
  if (shape === 'lower') {
    return (
      <SingleValueInput
        familyName={familyName}
        value={predicate.lower}
        onChange={(v) => setValue({ lower: v })}
        classes={classes}
      />
    )
  }
  if (shape === 'upper') {
    return (
      <SingleValueInput
        familyName={familyName}
        value={predicate.upper}
        onChange={(v) => setValue({ upper: v })}
        classes={classes}
      />
    )
  }
  if (shape === 'range') {
    return (
      <>
        <SingleValueInput
          familyName={familyName}
          value={predicate.lower}
          onChange={(v) => setValue({ lower: v })}
          classes={classes}
        />
        <span style={{ fontSize: 12 }}>and</span>
        <SingleValueInput
          familyName={familyName}
          value={predicate.upper}
          onChange={(v) => setValue({ upper: v })}
          classes={classes}
        />
      </>
    )
  }
  return null
}

ValueEditor.propTypes = {
  shape: PropTypes.string.isRequired,
  predicate: PropTypes.object.isRequired,
  familyName: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

/**
 * Multi-value editor for haystack/needles -- each entry has its own input
 * row plus add/remove controls. We deliberately avoid a delimiter-parsing
 * approach (comma-split, etc.) because account names and other Orion
 * group-info values legitimately contain commas; splitting on them would
 * silently mangle user input.
 */
function ListInput ({ items, onChange, classes, placeholder }) {
  // Memoize so the three useCallback hooks below don't see a fresh
  // reference on every parent render (the `[''] ` fallback array would
  // otherwise be a new object each time, invalidating every callback).
  const safeItems = useMemo(() => (items.length > 0 ? items : ['']), [items])

  const updateItem = useCallback((idx, nextValue) => {
    const next = [...safeItems]
    next[idx] = nextValue
    // Strip empty trailing entries on commit so the wire format stays clean,
    // but keep at least one slot visible so the user always has somewhere
    // to type.
    onChange(next.filter(s => s !== '').length === 0 ? [] : next.filter(s => s !== ''))
  }, [safeItems, onChange])

  const removeItem = useCallback((idx) => {
    const next = safeItems.filter((_, i) => i !== idx)
    onChange(next)
  }, [safeItems, onChange])

  const addItem = useCallback(() => {
    onChange([...safeItems.filter(Boolean), ''])
  }, [safeItems, onChange])

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
      {safeItems.map((value, idx) => (
        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input
            className={classes.predicateField}
            placeholder={placeholder}
            value={value}
            onChange={(e) => updateItem(idx, e.target.value)}
            style={{ flex: 1 }}
          />
          {safeItems.length > 1 && (
            <span
              className={classes.removeBtn}
              onClick={() => removeItem(idx)}
              role='button'
              tabIndex={0}
              aria-label='Remove value'
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeItem(idx) } }}
            >
              <Icon name='close' customSize={12} />
            </span>
          )}
        </span>
      ))}
      <button
        type='button'
        className={classes.addBtn}
        onClick={addItem}
        style={{ alignSelf: 'flex-start' }}
      >
        + Add value
      </button>
    </span>
  )
}

ListInput.propTypes = {
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  placeholder: PropTypes.string
}

function SingleValueInput ({ familyName, value, onChange, classes }) {
  if (familyName === 'bool') {
    return (
      <select
        className={classes.predicateField}
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) => {
          const v = e.target.value
          onChange(v === '' ? undefined : v === 'true')
        }}
      >
        {/* Hidden placeholder matches the empty `value` when the predicate
            is unset; predicates with undefined value are pruned by
            predicateHasUsableValue in scopeFilterCompile.js, so the
            unselected state composes with the rest of the pipeline
            without emitting an implicit false. */}
        <option value='' hidden>Select…</option>
        <option value='true'>true</option>
        <option value='false'>false</option>
      </select>
    )
  }
  if (familyName === 'date') {
    return (
      <input
        className={classes.predicateField}
        type='date'
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    )
  }
  if (familyName === 'datetime') {
    return (
      <input
        className={classes.predicateField}
        type='datetime-local'
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    )
  }
  // Numeric / decimal / currency / percent: numeric input, but keep the
  // raw text so the user can type partial values like "-" or "0." without
  // the field jumping.
  return (
    <input
      className={classes.predicateField}
      type='number'
      step='any'
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      style={{ width: 100 }}
    />
  )
}

SingleValueInput.propTypes = {
  familyName: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

/**
 * Per-predicate change override -- v1 surfaces only date overrides
 * (start/end). Anything beyond that is reachable by editing the saved
 * view JSON; a richer panel is a follow-up if real-world usage justifies
 * it.
 */
function ChangeOverrideEditor ({ predicate, setValue, classes }) {
  // Memoize the `{}` fallback so the setStart/setEnd callbacks don't
  // recreate every render when `predicate.change` is undefined.
  const change = useMemo(() => predicate.change || {}, [predicate.change])
  const setStart = useCallback((v) => {
    const next = { ...change }
    if (v) next.startDate = v
    else delete next.startDate
    setValue({ change: Object.keys(next).length ? next : undefined })
  }, [change, setValue])
  const setEnd = useCallback((v) => {
    const next = { ...change }
    if (v) next.endDate = v
    else delete next.endDate
    setValue({ change: Object.keys(next).length ? next : undefined })
  }, [change, setValue])
  return (
    <div className={classes.advancedField}>
      <label>Date override:</label>
      <input
        className={classes.predicateField}
        type='date'
        value={change.startDate || ''}
        onChange={(e) => setStart(e.target.value)}
      />
      <span>to</span>
      <input
        className={classes.predicateField}
        type='date'
        value={change.endDate || ''}
        onChange={(e) => setEnd(e.target.value)}
      />
    </div>
  )
}

ChangeOverrideEditor.propTypes = {
  predicate: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}
